import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiTrash2, FiPlus, FiEdit2 } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import AdminContainer from "../components/AdminContainer";
import AdminTitle from "../components/utils/AdminTitle";
import {
  createCoupon,
  deleteCoupon,
  getCoupons,
} from "../store/slices/coupons/asyncThunks";
import { selectCoupons } from "../store/slices/coupons/slice";
import AdminToast from "../components/utils/AdminToast";
import couponsService from "../api/services/coupons";

const CouponPage = () => {
  const dispatch = useDispatch();
  const coupons = useSelector(selectCoupons);

  const [toast, setToast] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const couponRef = useRef(null);

  const [editCoupon, setEditCoupon] = useState(null);
  const [editCode, setEditCode] = useState("");
  const [editDiscount, setEditDiscount] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const handleUpdateCoupon = () => {
    if (editLoading) return;
    const parsed = parseInt(editDiscount, 10);
    if (!editCode.trim() || isNaN(parsed) || parsed < 1 || parsed > 100) {
      setToast({
        type: "error",
        message: "Kod i popust (1–100) su obavezni.",
      });
      return;
    }
    setEditLoading(true);
    couponsService
      .updateCoupon(editCoupon.id, { code: editCode.trim(), discount: parsed })
      .then(() => {
        setEditCoupon(null);
        setToast({ type: "success", message: "Kupon je izmenjen." });
        dispatch(getCoupons());
      })
      .catch((err) =>
        setToast({ type: "error", message: err.message || "Greška pri izmeni." })
      )
      .finally(() => setEditLoading(false));
  };

  useEffect(() => {
    dispatch(getCoupons())
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message));
  }, [dispatch]);

  const handleCreateCoupon = () => {
    if (!couponCode.trim()) {
      setToast({ type: "error", message: "Unesi kupon kod." });
      couponRef.current?.focus();
      return;
    }
    if (coupons.createLoading) return;

    const parsedDiscount = parseInt(discount, 10);
    if (isNaN(parsedDiscount) || parsedDiscount < 1 || parsedDiscount > 100) {
      setToast({
        type: "error",
        message: "Popust mora biti broj između 1 i 100.",
      });
      return;
    }

    dispatch(createCoupon({ code: couponCode, discount: parsedDiscount }))
      .unwrap()
      .then(() => {
        setCouponCode("");
        setDiscount("");
        setToast({ type: "success", message: "Kupon je kreiran." });
      })
      .catch((err) =>
        setToast({
          type: "error",
          message: err.message || "Greška pri kreiranju kupona.",
        })
      );
  };

  useEffect(() => {
    if (deleteModal) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [deleteModal]);

  return (
    <AdminContainer>
      {toast && (
        <AdminToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <AdminTitle kanji="券">Kuponi</AdminTitle>

      <CreateRow>
        <Input
          ref={couponRef}
          placeholder="Kupon kod (npr. LETO10)…"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreateCoupon();
          }}
        />
        <Input
          $small
          placeholder="Popust %"
          type="number"
          min="1"
          max="100"
          value={discount}
          onChange={(e) => setDiscount(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreateCoupon();
          }}
        />
        <ButtonAdd
          $disabled={!couponCode.trim() || !discount || coupons.createLoading}
          onClick={handleCreateCoupon}
        >
          {coupons.createLoading ? (
            <l-line-spinner size="14" stroke="2" speed="1" color="#fe5f55" />
          ) : (
            <FiPlus size={16} />
          )}
          Dodaj
        </ButtonAdd>
      </CreateRow>

      {coupons.loading ? (
        <l-line-spinner size="32" stroke="3" speed="1" color="#fe5f55" />
      ) : coupons?.data?.length === 0 ? (
        <Empty>Nema kreiranih kupona.</Empty>
      ) : (
        <Grid>
          {coupons?.data?.map((coupon) => (
            <Card key={coupon?.id}>
              <CardTop>
                <Code>{coupon?.code}</Code>
                <DiscountPill>-{coupon?.discount}%</DiscountPill>
              </CardTop>
              <CardBottom>
                <Usage>
                  Iskorišćen <b>{coupon?.timesUsed ?? 0}×</b>
                </Usage>
                <BtnGroup>
                  <IconBtn
                    title="Izmeni kupon"
                    onClick={() => {
                      setEditCoupon(coupon);
                      setEditCode(coupon?.code || "");
                      setEditDiscount(String(coupon?.discount ?? ""));
                    }}
                  >
                    <FiEdit2 size={15} />
                  </IconBtn>
                  <IconBtn
                    title="Obriši kupon"
                    onClick={() => {
                      setSelectedCoupon(coupon);
                      setDeleteModal(true);
                    }}
                  >
                    <FiTrash2 size={15} />
                  </IconBtn>
                </BtnGroup>
              </CardBottom>
            </Card>
          ))}
        </Grid>
      )}

      <AnimatePresence>
        {editCoupon && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !editLoading && setEditCoupon(null)}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={() => !editLoading && setEditCoupon(null)}>
                <FaTimes />
              </CloseBtn>
              <h3>Izmeni kupon</h3>
              <ModalInput
                value={editCode}
                onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                placeholder="Kupon kod"
                autoFocus
              />
              <ModalInput
                type="number"
                min="1"
                max="100"
                value={editDiscount}
                onChange={(e) => setEditDiscount(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdateCoupon();
                }}
                placeholder="Popust %"
              />
              <ModalActions>
                <Cancel onClick={() => !editLoading && setEditCoupon(null)}>
                  Odustani
                </Cancel>
                <Confirm
                  $disabled={editLoading}
                  onClick={handleUpdateCoupon}
                >
                  {editLoading ? "Čuvanje…" : "Sačuvaj"}
                </Confirm>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!coupons.deleteLoading) {
                setDeleteModal(false);
              }
            }}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn
                onClick={() => {
                  if (!coupons.deleteLoading) {
                    setDeleteModal(false);
                  }
                }}
              >
                <FaTimes />
              </CloseBtn>
              <h3>Obriši kupon</h3>
              <p>
                Sigurno brišeš kupon <b>{selectedCoupon?.code}</b>? Kupci više
                neće moći da ga iskoriste.
              </p>
              <ModalActions>
                <Cancel
                  onClick={() => {
                    if (!coupons.deleteLoading) {
                      setDeleteModal(false);
                    }
                  }}
                >
                  Odustani
                </Cancel>
                {!coupons.deleteLoading ? (
                  <Confirm
                    onClick={() => {
                      dispatch(deleteCoupon(selectedCoupon?.id))
                        .unwrap()
                        .then(() => {
                          setDeleteModal(false);
                          setToast({
                            type: "success",
                            message: "Kupon je obrisan.",
                          });
                        })
                        .catch((err) =>
                          setToast({
                            type: "error",
                            message: err.message || "Greška pri brisanju.",
                          })
                        );
                    }}
                  >
                    Obriši
                  </Confirm>
                ) : (
                  <Confirm $disabled>
                    Obriši
                    <l-line-spinner
                      size="14"
                      stroke="2"
                      speed="1"
                      color="#fff"
                    />
                  </Confirm>
                )}
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </AdminContainer>
  );
};

export default CouponPage;

const CreateRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
  max-width: 62rem;
  margin-bottom: 1.4rem;
`;

const Input = styled.input`
  flex: ${(props) => (props.$small ? "0 0 12rem" : "1")};
  min-width: ${(props) => (props.$small ? "12rem" : "20rem")};
  padding: 0 1.4rem;
  height: 4.4rem;
  background: var(--bg1, #101013);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: var(--text, #f4f2ee);
  border-radius: 0.4rem;
  font-family: "Poppins-Regular";
  font-size: 1.3rem;
  outline: none;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

const ButtonAdd = styled.button`
  height: 4.4rem;
  padding: 0 2rem;
  background: ${(props) =>
    props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red, #fe5f55)"};
  color: ${(props) =>
    props.$disabled ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  border: 1px solid
    ${(props) =>
      props.$disabled ? "var(--line2, rgba(255,255,255,0.14))" : "transparent"};
  border-radius: 0.4rem;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.2rem;
  transition: background 0.25s var(--ease, ease);

  &:hover {
    background: ${(props) =>
      props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red-deep, #e04a40)"};
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const Empty = styled.div`
  font-family: "Poppins-Medium";
  font-size: 1.3rem;
  color: var(--muted, #8e8c94);
  padding: 2rem 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(26rem, 1fr));
  gap: 1.2rem;
  width: 100%;
`;

const Card = styled.div`
  background: var(--bg1, #101013);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.4rem;
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  transition: border-color 0.25s;

  &:hover {
    border-color: var(--line2, rgba(255, 255, 255, 0.14));
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const Code = styled.div`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  font-size: 2rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text, #f4f2ee);
`;

const DiscountPill = styled.span`
  font-family: "Poppins-Bold";
  font-size: 1.2rem;
  color: var(--red, #fe5f55);
  background: rgba(254, 95, 85, 0.1);
  border: 1px solid rgba(254, 95, 85, 0.3);
  border-radius: 10rem;
  padding: 0.4rem 1.1rem;
`;

const CardBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-top: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  padding-top: 1.2rem;
`;

const Usage = styled.div`
  font-family: "Poppins-Regular";
  font-size: 1.25rem;
  color: var(--muted, #8e8c94);

  b {
    font-family: "Poppins-SemiBold";
    color: var(--text, #f4f2ee);
  }
`;

const BtnGroup = styled.div`
  display: flex;
  gap: 0.7rem;
`;

const IconBtn = styled.button`
  width: 3.2rem;
  height: 3.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;
  color: var(--muted, #8e8c94);
  cursor: pointer;
  transition: all 0.25s;

  &:hover {
    color: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
  }
`;

const ModalInput = styled.input`
  width: 100%;
  height: 4.4rem;
  padding: 0 1.4rem;
  margin-bottom: 1.2rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: var(--text, #f4f2ee);
  border-radius: 0.4rem;
  font-family: "Poppins-Regular";
  font-size: 1.4rem;
  outline: none;
  transition: border-color 0.25s;

  &:last-of-type {
    margin-bottom: 2rem;
  }

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(6, 6, 8, 0.8);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1.4rem;
  right: 1.4rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: #fff;
  border-radius: 50%;
  font-size: 1.3rem;
  width: 3.2rem;
  height: 3.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s;

  &:hover {
    background: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
    color: var(--bg0, #0a0a0c);
  }
`;

const ConfirmBox = styled.div`
  background: var(--bg2, #17171c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  padding: 3rem 2.4rem;
  border-radius: 0.6rem;
  max-width: 42rem;
  width: 100%;
  color: var(--text, #f4f2ee);
  position: relative;
  text-align: center;

  h3 {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 2rem;
    margin-bottom: 1.2rem;
  }

  p {
    font-family: "Poppins-Regular";
    font-size: 1.35rem;
    line-height: 1.6;
    margin-bottom: 2.2rem;
    color: var(--muted, #8e8c94);

    b {
      font-family: "Poppins-SemiBold";
      color: var(--red, #fe5f55);
    }
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const Cancel = styled.button`
  padding: 1.1rem 2.2rem;
  border-radius: 0.4rem;
  background: transparent;
  color: var(--muted, #8e8c94);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.25s;

  &:hover {
    color: var(--text, #f4f2ee);
    border-color: var(--text, #f4f2ee);
  }
`;

const Confirm = styled.button`
  padding: 1.1rem 2.2rem;
  border-radius: 0.4rem;
  background: var(--red, #fe5f55);
  color: var(--bg0, #0a0a0c);
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.2rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: background 0.25s;

  &:hover {
    background: var(--red-deep, #e04a40);
  }
`;
